import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import LanguageSwitcher from './LanguageSwitcher';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Progress } from './ui/progress';
import InvestorForm from './InvestorForm';
import InvestorMatches from './InvestorMatches';
import ScoreBreakdownModal from './ScoreBreakdownModal';
import { 
  Plus,
  Edit,
  Eye,
  CheckCircle,
  XCircle,
  Building2,
  MapPin,
  Globe,
  Mail,
  Phone,
  Award,
  Search,
  Filter,
  TrendingUp,
  DollarSign,
  Target,
  Users,
  Briefcase,
  BarChart3,
  RefreshCw,
  Trash2,
  Calculator,
  Shield,
  ShieldCheck,
  ShieldX,
  Clock,
  AlertCircle,
  Download,
  FileText,
  ArrowLeft,
  Upload
} from 'lucide-react';
import { toast } from 'sonner';

const API = BACKEND_URL;

const investorTypeLabels = {
  company: 'Company',
  investment_fund: 'Investment Fund',
  philanthropic_foundation: 'Philanthropic Foundation',
  family_office: 'Family Office',
  individual: 'Individual Investor'
};

const statusLabels = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
  pending_approval: { label: 'Pending Approval', color: 'bg-yellow-100 text-yellow-800' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800' },
  certified: { label: 'Certified', color: 'bg-blue-100 text-blue-800' }
};

const certificationLabels = {
  none: { label: 'Not Certified', color: 'bg-gray-100 text-gray-700', icon: Shield },
  level_1_basic: { label: 'Level C - Basic', color: 'bg-amber-100 text-amber-800', icon: Shield },
  level_2_commitment: { label: 'Level B - Commitment', color: 'bg-blue-100 text-blue-800', icon: ShieldCheck },
  level_3_excellence: { label: 'Level A - Excellence', color: 'bg-emerald-100 text-emerald-800', icon: ShieldCheck }
};

const scoreLabels = {
  investor_profile: 'Investor Profile',
  social_mission: 'Social Mission & CSR',
  investment_commitment: 'Investment Commitment',
  non_financial: 'Non-Financial Support',
  impact_management: 'Impact Management',
  esg_criteria: 'ESG Criteria',
  sdg_alignment: 'SDG Alignment',
  historical_investments: 'Historical Investments',
  transparency: 'Transparency'
};

const SocialInvestorRegistry = ({ user }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [stats, setStats] = useState(null);
  const [showBreakdown, setShowBreakdown] = useState(false);

  const fetchInvestors = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType !== 'all') params.append('investor_type', filterType);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      
      const response = await axios.get(`${API}/api/social-investors?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setInvestors(response.data);
    } catch (error) {
      console.error('Failed to fetch investors:', error);
      toast.error('Failed to load investors');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/api/social-investors/stats/summary`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  useEffect(() => {
    fetchInvestors();
    if (user?.role === 'administrator' || user?.role === 'project_manager') {
      fetchStats();
    }
  }, [filterType, filterStatus]);

  const handleCreateSuccess = () => {
    setShowCreateDialog(false);
    fetchInvestors();
    toast.success('Investor profile created successfully!');
  };

  const handleUpdateSuccess = () => {
    setShowEditDialog(false);
    fetchInvestors();
    if (selectedInvestor) {
      viewInvestorDetails(selectedInvestor.id);
    }
    toast.success('Investor profile updated successfully!');
  };

  const viewInvestorDetails = async (investorId) => {
    try {
      const response = await axios.get(`${API}/api/social-investors/${investorId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSelectedInvestor(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      console.error('Failed to fetch investor details:', error);
      toast.error('Failed to load investor details');
    }
  };

  const handleSubmitForApproval = async (investorId) => {
    try {
      await axios.post(`${API}/api/social-investors/${investorId}/submit`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Profile submitted for approval');
      fetchInvestors();
      if (selectedInvestor?.id === investorId) {
        viewInvestorDetails(investorId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit for approval');
    }
  };

  const handleReview = async (investorId, action, reason = null) => {
    try {
      const params = new URLSearchParams({ action });
      if (reason) params.append('reason', reason);
      
      await axios.post(`${API}/api/social-investors/${investorId}/review?${params}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success(`Profile ${action}d successfully`);
      fetchInvestors();
      fetchStats();
      if (selectedInvestor?.id === investorId) {
        viewInvestorDetails(investorId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} profile`);
    }
  };

  const handleDelete = async (investorId) => {
    if (!confirm('Are you sure you want to delete this investor profile?')) return;
    
    try {
      await axios.delete(`${API}/api/social-investors/${investorId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Investor deleted successfully');
      setShowDetailDialog(false);
      fetchInvestors();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete investor');
    }
  };

  const handleCalculateScore = async (investorId) => {
    try {
      const response = await axios.post(`${API}/api/social-investors/${investorId}/calculate-score`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const d = response.data || {};
      if (d.status === 'pending_review') {
        toast.success(`Self-assessment of ${d.scores?.overall ?? 0}% submitted. Pending PM/Admin approval — your published score will not change until approved.`);
      } else {
        toast.success(`Score calculated: ${d.scores?.overall ?? 0}%`);
      }
      viewInvestorDetails(investorId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to calculate score');
    }
  };

  const handleApprovePendingAssessment = async (investorId, updateCertification = false) => {
    try {
      const res = await axios.post(
        `${API}/api/social-investors/${investorId}/pending-assessment/approve?update_certification=${updateCertification ? 'true' : 'false'}`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (res.data?.success) {
        toast.success('Pending self-assessment approved.');
        viewInvestorDetails(investorId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve pending assessment.');
    }
  };

  const handleRejectPendingAssessment = async (investorId, reason = '') => {
    try {
      const res = await axios.post(
        `${API}/api/social-investors/${investorId}/pending-assessment/reject`,
        { reason },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (res.data?.success) {
        toast.success('Pending self-assessment rejected.');
        viewInvestorDetails(investorId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject pending assessment.');
    }
  };

  const handleRequestCertification = async (investorId) => {
    try {
      const response = await axios.post(`${API}/api/social-investors/${investorId}/request-certification`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data.success) {
        toast.success('Certification requested successfully');
      } else {
        toast.error(response.data.message || 'Unable to request certification');
      }
      viewInvestorDetails(investorId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request certification');
    }
  };

  const handleCertify = async (investorId, level = null) => {
    try {
      const params = new URLSearchParams();
      if (level) params.append('certification_level', level);
      
      const response = await axios.post(`${API}/api/social-investors/${investorId}/certify?${params}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success(response.data.message || 'Investor certified successfully');
      fetchInvestors();
      fetchStats();
      viewInvestorDetails(investorId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to certify investor');
    }
  };

  const handleRevokeCertification = async (investorId) => {
    const reason = prompt('Please provide a reason for revoking this certification:');
    if (!reason) return;
    
    try {
      await axios.post(`${API}/api/social-investors/${investorId}/revoke-certification`, null, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: { reason }
      });
      toast.success('Certification revoked');
      fetchInvestors();
      fetchStats();
      viewInvestorDetails(investorId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke certification');
    }
  };

  const handleDownloadCertificate = async (investorId) => {
    try {
      const response = await axios.get(`${API}/api/social-investors/${investorId}/certificate/pdf`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `investor_certificate_${investorId.substring(0,8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Certificate downloaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download certificate');
    }
  };

  const filteredInvestors = investors.filter(inv =>
    inv.organization_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const canEdit = (investor) => {
    if (user?.role === 'administrator' || user?.role === 'project_manager') return true;
    if (user?.role === 'investor' && investor.user_id === user?.id) return true;
    return false;
  };

  const isOwner = (investor) => investor.user_id === user?.id;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            data-testid="back-to-dashboard-btn"
            variant="outline"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('social_investor_registry.back_to_dashboard')}
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Briefcase className="h-7 w-7 text-emerald-700" />
              {t('social_investor_registry.title')}
            </h1>
            <p className="text-gray-600 mt-1">
              {t('social_investor_registry.subtitle')}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          {(user?.role === 'investor' || user?.role === 'administrator' || user?.role === 'project_manager') && (
            <Button onClick={() => setShowCreateDialog(true)} className="bg-emerald-800 hover:bg-emerald-900 text-white">
              <Plus className="h-4 w-4 mr-2" />
              {t('social_investor_registry.register_investor')}
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards (Admin/PM only) */}
      {stats && (user?.role === 'administrator' || user?.role === 'project_manager') && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <span className="block text-3xl font-bold text-emerald-700">{stats.total}</span>
              <p className="text-sm text-gray-600">{t('social_investor_registry.stat_total_investors')}</p>
            </CardContent>
          </Card>
          <Card className="bg-yellow-50">
            <CardContent className="p-4 text-center">
              <span className="block text-3xl font-bold text-yellow-700">{stats.by_status?.pending_approval || 0}</span>
              <p className="text-sm text-yellow-700">{t('social_investor_registry.stat_pending_review')}</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50">
            <CardContent className="p-4 text-center">
              <span className="block text-3xl font-bold text-green-700">{stats.by_status?.approved || 0}</span>
              <p className="text-sm text-green-700">{t('social_investor_registry.stat_approved')}</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50">
            <CardContent className="p-4 text-center">
              <span className="block text-3xl font-bold text-blue-700">{stats.public_profiles || 0}</span>
              <p className="text-sm text-blue-600">{t('social_investor_registry.stat_public_profiles')}</p>
            </CardContent>
          </Card>
          <Card className="bg-emerald-50">
            <CardContent className="p-4 text-center">
              <span className="block text-2xl font-bold text-emerald-700">{formatCurrency(stats.total_capital_available)}</span>
              <p className="text-sm text-emerald-700">{t('social_investor_registry.stat_total_capital')}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" aria-hidden="true" />
              <label htmlFor="sir-search-input" className="sr-only">
                {t('social_investor_registry.search_placeholder')}
              </label>
              <Input
                id="sir-search-input"
                placeholder={t('social_investor_registry.search_placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="sir-search-input"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full md:w-48" aria-label={t('social_investor_registry.filter_type_placeholder')}>
                <SelectValue placeholder={t('social_investor_registry.filter_type_placeholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('social_investor_registry.all_types')}</SelectItem>
                <SelectItem value="company">{t('social_investor_registry.type_company')}</SelectItem>
                <SelectItem value="investment_fund">{t('social_investor_registry.type_investment_fund')}</SelectItem>
                <SelectItem value="philanthropic_foundation">{t('social_investor_registry.type_foundation')}</SelectItem>
                <SelectItem value="family_office">{t('social_investor_registry.type_family_office')}</SelectItem>
                <SelectItem value="individual">{t('social_investor_registry.type_individual')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full md:w-40" aria-label={t('social_investor_registry.filter_status_placeholder')}>
                <SelectValue placeholder={t('social_investor_registry.filter_status_placeholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('social_investor_registry.all_status')}</SelectItem>
                <SelectItem value="draft">{t('social_investor_registry.status_draft')}</SelectItem>
                <SelectItem value="pending_approval">{t('social_investor_registry.status_pending')}</SelectItem>
                <SelectItem value="approved">{t('social_investor_registry.status_approved')}</SelectItem>
                <SelectItem value="certified">{t('social_investor_registry.status_certified')}</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchInvestors} aria-label="Refresh investor list" data-testid="sir-refresh-btn">
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Investors Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-emerald-700" />
        </div>
      ) : filteredInvestors.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            <Briefcase className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg">No investors found</p>
            <p className="text-sm mt-1">Try adjusting your filters or create a new investor profile</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredInvestors.map((investor) => (
            <Card key={investor.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="bg-emerald-100 p-2 rounded-lg">
                      <Briefcase className="h-5 w-5 text-emerald-700" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 line-clamp-1">
                        {investor.organization_name}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {investorTypeLabels[investor.investor_type] || investor.investor_type}
                      </p>
                    </div>
                  </div>
                  {isOwner(investor) && (
                    <Badge className="bg-emerald-100 text-emerald-700 text-xs">Your Profile</Badge>
                  )}
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin className="h-4 w-4" />
                    <span>{investor.city || 'Unknown'}, {investor.country || 'Greece'}</span>
                  </div>
                  {investor.capital_available && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <DollarSign className="h-4 w-4" />
                      <span>{formatCurrency(investor.capital_available)} available</span>
                    </div>
                  )}
                  {investor.sdg_alignment && investor.sdg_alignment.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-gray-500" />
                      <div className="flex flex-wrap gap-1">
                        {investor.sdg_alignment.slice(0, 4).map(sdg => (
                          <Badge key={sdg} variant="outline" className="text-xs px-1.5">
                            SDG {sdg}
                          </Badge>
                        ))}
                        {investor.sdg_alignment.length > 4 && (
                          <Badge variant="outline" className="text-xs px-1.5">
                            +{investor.sdg_alignment.length - 4}
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge className={statusLabels[investor.status]?.color || 'bg-gray-100'}>
                      {statusLabels[investor.status]?.label || investor.status}
                    </Badge>
                    {investor.certification_level && investor.certification_level !== 'none' && (
                      <Badge className={certificationLabels[investor.certification_level]?.color}>
                        {certificationLabels[investor.certification_level]?.label}
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => viewInvestorDetails(investor.id)}
                      aria-label={`View ${investor.organization_name || 'investor'} details`}
                      data-testid={`view-investor-${investor.id}`}
                    >
                      <Eye className="h-4 w-4" aria-hidden="true" />
                    </Button>
                    {canEdit(investor) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedInvestor(investor);
                          setShowEditDialog(true);
                        }}
                        aria-label={`Edit ${investor.organization_name || 'investor'}`}
                        data-testid={`edit-investor-${investor.id}`}
                      >
                        <Edit className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-emerald-700" />
              {t('social_investor_registry.create_title')}
            </DialogTitle>
          </DialogHeader>
          <InvestorForm onSuccess={handleCreateSuccess} onCancel={() => setShowCreateDialog(false)} />
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-emerald-700" />
              {t('social_investor_registry.edit_title')}
            </DialogTitle>
          </DialogHeader>
          {selectedInvestor && (
            <div className="space-y-6">
              <InvestorForm
                investor={selectedInvestor}
                onSuccess={handleUpdateSuccess}
                onCancel={() => setShowEditDialog(false)}
              />

              {/* Supporting documents (PDF / Word) */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Supporting Documents</h4>
                    <p className="text-xs text-gray-500">Upload PDF or Word files (max 10 MB each)</p>
                  </div>
                  <input
                    id="investor-doc-upload"
                    type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (!file) return;
                      if (file.size > 10 * 1024 * 1024) {
                        toast.error('File exceeds 10 MB limit');
                        return;
                      }
                      const reader = new FileReader();
                      reader.onload = async () => {
                        const b64 = reader.result.toString().split(',')[1];
                        try {
                          await axios.post(
                            `${API}/api/social-investors/${selectedInvestor.id}/documents`,
                            { filename: file.name, content_type: file.type, file_data: b64 },
                            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
                          );
                          toast.success(`Uploaded ${file.name}`);
                          viewInvestorDetails(selectedInvestor.id);
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Upload failed');
                        }
                      };
                      reader.readAsDataURL(file);
                    }}
                    data-testid="investor-doc-upload-input"
                  />
                  <label htmlFor="investor-doc-upload">
                    <Button type="button" size="sm" variant="outline" asChild data-testid="investor-doc-upload-btn">
                      <span><Upload className="h-4 w-4 mr-1" />Upload</span>
                    </Button>
                  </label>
                </div>
                {(selectedInvestor.uploaded_documents || []).length === 0 ? (
                  <p className="text-xs text-gray-500 italic">No documents uploaded yet.</p>
                ) : (
                  <div className="space-y-1">
                    {selectedInvestor.uploaded_documents.map((doc) => (
                      <div key={doc.id} className="flex items-center gap-2 px-2 py-1 border rounded text-xs">
                        <FileText className="h-3.5 w-3.5 text-blue-600" />
                        <a
                          href={`${API}${doc.file_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 truncate hover:underline"
                        >
                          {doc.document_name || doc.filename}
                        </a>
                        <span className="text-gray-500">{doc.size_bytes ? `${Math.round(doc.size_bytes / 1024)} KB` : ''}</span>
                        <Button
                          size="sm" variant="ghost"
                          className="h-6 w-6 p-0 text-red-600"
                          onClick={async () => {
                            if (!window.confirm('Delete this document?')) return;
                            try {
                              await axios.delete(
                                `${API}/api/social-investors/${selectedInvestor.id}/documents/${doc.id}`,
                                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
                              );
                              toast.success('Document deleted');
                              viewInvestorDetails(selectedInvestor.id);
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Delete failed');
                            }
                          }}
                        >
                          <XCircle className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-emerald-700" />
              {selectedInvestor?.organization_name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedInvestor && (
            <Tabs defaultValue="profile" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="profile">{t('social_investor_registry.tab_profile')}</TabsTrigger>
                <TabsTrigger value="certification">
                  <Award className="h-4 w-4 mr-1" />
                  {t('social_investor_registry.tab_certification')}
                </TabsTrigger>
                <TabsTrigger value="matches">
                  <Target className="h-4 w-4 mr-1" />
                  {t('social_investor_registry.tab_matches')}
                </TabsTrigger>
                <TabsTrigger value="activity">{t('social_investor_registry.tab_activity')}</TabsTrigger>
              </TabsList>

              <TabsContent value="profile" className="mt-4 space-y-6">
                {/* Pending self-assessment banner */}
                {selectedInvestor.pending_assessment_scores && Object.keys(selectedInvestor.pending_assessment_scores).length > 0 && (
                  <div className="border border-amber-300 bg-amber-50 rounded-lg p-4" data-testid="investor-pending-assessment-banner">
                    <div className="flex items-center gap-2 text-amber-900 font-semibold mb-1">
                      <Clock className="h-5 w-5" /> Self-assessment pending review
                    </div>
                    {isOwner(selectedInvestor) ? (
                      <p className="text-sm text-amber-900">
                        Your newly calculated score is pending review by an Administrator or Project Manager.
                        Your organisation&apos;s published score and certification level will remain unchanged until approved.
                      </p>
                    ) : (
                      <p className="text-sm text-amber-900">
                        This investor submitted a self-assessment. Approve to publish the new scores; the certification
                        level only changes if you choose to update it.
                      </p>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-3">
                      {['overall','impact_alignment','financial_capacity','non_financial_value','transparency','social_orientation'].map(k => (
                        <div key={k} className="bg-white border rounded-md py-2 text-center">
                          <div className="text-lg font-bold text-amber-700">{selectedInvestor.pending_assessment_scores[k] ?? 0}%</div>
                          <div className="text-xs uppercase tracking-wide text-gray-500">{String(k).replace(/_/g,' ')}</div>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-wrap items-center justify-between mt-3 gap-3">
                      <p className="text-xs text-amber-900">
                        {selectedInvestor.pending_assessment_at && (
                          <>Submitted: {new Date(selectedInvestor.pending_assessment_at).toLocaleString('en-GB')}</>
                        )}
                        {selectedInvestor.pending_recommended_certification && (
                          <> · Recommended level: {String(selectedInvestor.pending_recommended_certification).replace(/_/g, ' ')}</>
                        )}
                      </p>
                      {(user?.role === 'administrator' || user?.role === 'project_manager') && (
                        <div className="flex flex-wrap items-center gap-2">
                          <Button size="sm"
                            className="bg-emerald-700 hover:bg-emerald-800 text-white"
                            onClick={() => handleApprovePendingAssessment(selectedInvestor.id, false)}
                            data-testid="investor-approve-pending-btn"
                          >
                            <CheckCircle className="h-4 w-4 mr-1" /> Approve scores
                          </Button>
                          {selectedInvestor.pending_recommended_certification &&
                            selectedInvestor.pending_recommended_certification !== selectedInvestor.certification_level && (
                            <Button size="sm"
                              className="bg-blue-600 hover:bg-blue-700 text-white"
                              onClick={() => handleApprovePendingAssessment(selectedInvestor.id, true)}
                              data-testid="investor-approve-pending-with-cert-btn"
                            >
                              <CheckCircle className="h-4 w-4 mr-1" /> Approve & update level
                            </Button>
                          )}
                          <Button size="sm" variant="outline"
                            className="border-red-300 text-red-700 hover:bg-red-50"
                            onClick={() => {
                              const reason = window.prompt('Optional reason for rejecting this self-assessment:') || '';
                              handleRejectPendingAssessment(selectedInvestor.id, reason);
                            }}
                            data-testid="investor-reject-pending-btn"
                          >
                            <XCircle className="h-4 w-4 mr-1" /> Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Status and Actions */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge className={`${statusLabels[selectedInvestor.status]?.color} px-3 py-1`}>
                      {statusLabels[selectedInvestor.status]?.label}
                    </Badge>
                    {selectedInvestor.certification_level && selectedInvestor.certification_level !== 'none' && (
                      <Badge className={`${certificationLabels[selectedInvestor.certification_level]?.color} px-3 py-1`}>
                        {certificationLabels[selectedInvestor.certification_level]?.label}
                      </Badge>
                    )}
                    {selectedInvestor.is_public && (
                      <Badge className="bg-blue-100 text-blue-800">{t('social_investor_registry.public_badge')}</Badge>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {selectedInvestor.status === 'draft' && canEdit(selectedInvestor) && (
                      <Button size="sm" onClick={() => handleSubmitForApproval(selectedInvestor.id)}>
                        {t('social_investor_registry.submit_for_approval')}
                      </Button>
                    )}
                    {selectedInvestor.status === 'pending_approval' && 
                     (user?.role === 'administrator' || user?.role === 'project_manager') && (
                      <>
                        <Button 
                          size="sm" 
                          className="bg-green-700 hover:bg-green-800"
                          onClick={() => handleReview(selectedInvestor.id, 'approve')}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          {t('social_investor_registry.approve')}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => handleReview(selectedInvestor.id, 'reject')}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          {t('social_investor_registry.reject')}
                        </Button>
                      </>
                    )}
                    {canEdit(selectedInvestor) && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          setShowDetailDialog(false);
                          setShowEditDialog(true);
                        }}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        {t('social_investor_registry.edit')}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Profile Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Basic Info */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        {t('social_investor_registry.card_org_details')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div><span className="text-gray-500">{t('social_investor_registry.label_type')}</span> {investorTypeLabels[selectedInvestor.investor_type]}</div>
                      <div><span className="text-gray-500">{t('social_investor_registry.label_legal_form')}</span> {selectedInvestor.legal_form || 'N/A'}</div>
                      <div><span className="text-gray-500">{t('social_investor_registry.label_year_established')}</span> {selectedInvestor.year_established || 'N/A'}</div>
                      <div><span className="text-gray-500">{t('social_investor_registry.label_location')}</span> {selectedInvestor.city}, {selectedInvestor.country}</div>
                      <div><span className="text-gray-500">{t('social_investor_registry.label_website')}</span> {selectedInvestor.website || 'N/A'}</div>
                    </CardContent>
                  </Card>

                  {/* Investment Profile */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <DollarSign className="h-4 w-4" />
                        {t('social_investor_registry.card_investment_profile')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div><span className="text-gray-500">{t('social_investor_registry.label_capital_available')}</span> {formatCurrency(selectedInvestor.capital_available)}</div>
                      <div><span className="text-gray-500">{t('social_investor_registry.label_investment_range')}</span> {formatCurrency(selectedInvestor.minimum_investment_size)} - {formatCurrency(selectedInvestor.maximum_investment_size)}</div>
                      <div><span className="text-gray-500">{t('social_investor_registry.label_time_horizon')}</span> {selectedInvestor.time_horizon ? String(selectedInvestor.time_horizon).replace(/_/g, ' ') : 'N/A'}</div>
                      <div><span className="text-gray-500">{t('social_investor_registry.label_expected_return')}</span> {selectedInvestor.expected_return != null && selectedInvestor.expected_return !== '' ? (typeof selectedInvestor.expected_return === 'number' ? `${selectedInvestor.expected_return}%` : String(selectedInvestor.expected_return).replace(/_/g, ' ')) : 'N/A'}</div>
                      <div><span className="text-gray-500">{t('social_investor_registry.label_risk_appetite')}</span> {selectedInvestor.risk_appetite || 'N/A'}</div>
                    </CardContent>
                  </Card>

                  {/* SDG Alignment */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Target className="h-4 w-4" />
                        {t('social_investor_registry.card_sdg_alignment')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {[...(selectedInvestor.primary_sdgs || []), ...(selectedInvestor.sdg_alignment || [])].filter((v, i, a) => a.indexOf(v) === i).map(sdg => (
                          <Badge key={sdg} className="bg-emerald-100 text-emerald-800">
                            SDG {sdg}
                          </Badge>
                        ))}
                        {(!selectedInvestor.sdg_alignment || selectedInvestor.sdg_alignment.length === 0) && 
                         (!selectedInvestor.primary_sdgs || selectedInvestor.primary_sdgs.length === 0) && (
                          <span className="text-gray-500 text-sm">{t('social_investor_registry.no_sdgs')}</span>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Non-Financial */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        {t('social_investor_registry.card_non_financial')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div><span className="text-gray-500">{t('social_investor_registry.label_mentoring_hours')}</span> {selectedInvestor.mentoring_hours_annual || 'N/A'}</div>
                      <div><span className="text-gray-500">{t('social_investor_registry.label_governance_participation')}</span> {selectedInvestor.governance_participation ? t('social_investor_registry.yes') : t('social_investor_registry.no')}</div>
                      <div><span className="text-gray-500">{t('social_investor_registry.label_expertise_areas')}</span> {selectedInvestor.expertise_areas?.join(', ') || 'N/A'}</div>
                    </CardContent>
                  </Card>
                </div>

                {/* Social Mission */}
                {selectedInvestor.social_mission && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">{t('social_investor_registry.card_social_mission')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-700">{selectedInvestor.social_mission}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Historical Investments */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" />
                      {t('social_investor_registry.card_track_record')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div><span className="text-gray-500">{t('social_investor_registry.label_previous_investments')}</span> {selectedInvestor.previous_investments_count || 'N/A'}</div>
                    <div><span className="text-gray-500">{t('social_investor_registry.label_total_capital_deployed')}</span> {formatCurrency(selectedInvestor.total_capital_deployed)}</div>
                    <div><span className="text-gray-500">{t('social_investor_registry.label_years_experience')}</span> {selectedInvestor.investment_period_years || 'N/A'}</div>
                    <div><span className="text-gray-500">{t('social_investor_registry.label_beneficiaries_reached')}</span> {selectedInvestor.beneficiaries_reached?.toLocaleString('el-GR') || 'N/A'}</div>
                    <div><span className="text-gray-500">{t('social_investor_registry.label_jobs_created')}</span> {selectedInvestor.jobs_created?.toLocaleString('el-GR') || 'N/A'}</div>
                  </CardContent>
                </Card>

                {/* Documents card — full width */}
                <Card data-testid="investor-documents-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Documents
                      {(selectedInvestor.uploaded_documents || []).length > 0 && (
                        <Badge variant="secondary" className="ml-1">{selectedInvestor.uploaded_documents.length}</Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {(selectedInvestor.uploaded_documents || []).length === 0 ? (
                      <p className="text-sm text-gray-500 italic">No documents have been uploaded yet.</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {selectedInvestor.uploaded_documents.map((doc) => (
                          <div key={doc.id} className="flex items-center gap-2 p-2 border rounded-md hover:bg-gray-50 transition-colors">
                            <a
                              href={`${API}${doc.file_url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-1 min-w-0 flex items-center gap-2 text-sm"
                              data-testid={`investor-doc-${doc.id}`}
                            >
                              <FileText className="h-4 w-4 text-blue-600 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{doc.document_name || doc.filename}</div>
                                <div className="text-xs text-gray-500 truncate">
                                  {doc.document_type ? `${doc.document_type} · ` : ''}
                                  {doc.size_bytes ? `${Math.round(doc.size_bytes / 1024)} KB` : ''}
                                  {doc.uploaded_at ? ` · ${new Date(doc.uploaded_at).toLocaleDateString('en-GB')}` : ''}
                                </div>
                              </div>
                              <Download className="h-4 w-4 text-gray-500 shrink-0" />
                            </a>
                            {canEdit(selectedInvestor) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:bg-red-50"
                                onClick={async () => {
                                  if (!window.confirm('Delete this document?')) return;
                                  try {
                                    await axios.delete(`${API}/api/social-investors/${selectedInvestor.id}/documents/${doc.id}`, {
                                      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                    });
                                    toast.success('Document deleted');
                                    viewInvestorDetails(selectedInvestor.id);
                                  } catch (e) {
                                    toast.error(e.response?.data?.detail || 'Failed to delete document');
                                  }
                                }}
                                data-testid={`investor-doc-delete-${doc.id}`}
                              >
                                <XCircle className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {canEdit(selectedInvestor) && (
                      <div className="mt-4 border-t pt-3">
                        <Label htmlFor="investor-doc-upload" className="text-sm font-medium">Upload new document</Label>
                        <input
                          id="investor-doc-upload"
                          type="file"
                          className="mt-1 block w-full text-sm text-gray-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            if (file.size > 10 * 1024 * 1024) {
                              toast.error('File exceeds 10 MB limit');
                              e.target.value = '';
                              return;
                            }
                            const reader = new FileReader();
                            reader.onload = async () => {
                              const b64 = String(reader.result || '').split(',')[1] || '';
                              try {
                                await axios.post(
                                  `${API}/api/social-investors/${selectedInvestor.id}/documents`,
                                  {
                                    filename: file.name,
                                    content_type: file.type || 'application/octet-stream',
                                    file_data: b64,
                                    document_name: file.name.replace(/\.[^.]+$/, ''),
                                    document_type: 'supporting'
                                  },
                                  { headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' } }
                                );
                                toast.success('Document uploaded');
                                e.target.value = '';
                                viewInvestorDetails(selectedInvestor.id);
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Upload failed');
                              }
                            };
                            reader.readAsDataURL(file);
                          }}
                          data-testid="investor-doc-upload-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">Max 10 MB. PDF / DOCX / images all supported.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
                <div className="hidden">{/* end of documents card */}</div>
              </TabsContent>

              {/* Certification Tab */}
              <TabsContent value="certification" className="mt-4 space-y-6">
                {/* Current Certification Status */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Award className="h-4 w-4" />
                      {t('social_investor_registry.cert_card_status_title')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        {(() => {
                          const certLevel = selectedInvestor.certification_level || 'none';
                          const CertIcon = certificationLabels[certLevel]?.icon || Shield;
                          return (
                            <>
                              <CertIcon className={`h-8 w-8 ${certLevel !== 'none' ? 'text-emerald-700' : 'text-gray-500'}`} />
                              <div>
                                <p className="font-medium">{certificationLabels[certLevel]?.label || t('social_investor_registry.not_certified')}</p>
                                {selectedInvestor.certification_date && (
                                  <p className="text-sm text-gray-500">
                                    {t('social_investor_registry.cert_certified_on', { date: new Date(selectedInvestor.certification_date).toLocaleDateString('en-GB') })}
                                  </p>
                                )}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                      <Badge className={certificationLabels[selectedInvestor.certification_level || 'none']?.color}>
                        {certificationLabels[selectedInvestor.certification_level || 'none']?.label}
                      </Badge>
                    </div>

                    {/* Certification Actions */}
                    <div className="flex flex-wrap gap-2 pt-4 border-t">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleCalculateScore(selectedInvestor.id)}
                        data-testid="calculate-score-btn"
                      >
                        <Calculator className="h-4 w-4 mr-1" />
                        {t('social_investor_registry.cert_calculate_score')}
                      </Button>
                      
                      {selectedInvestor.status === 'approved' && 
                       (!selectedInvestor.certification_level || selectedInvestor.certification_level === 'none') && (
                        <Button 
                          size="sm"
                          onClick={() => handleRequestCertification(selectedInvestor.id)}
                          data-testid="request-certification-btn"
                        >
                          <Award className="h-4 w-4 mr-1" />
                          {t('social_investor_registry.cert_request')}
                        </Button>
                      )}
                      
                      {(user?.role === 'administrator' || user?.role === 'project_manager') && 
                       selectedInvestor.status === 'approved' && (
                        <Button 
                          size="sm"
                          className="bg-emerald-700 hover:bg-emerald-800"
                          onClick={() => handleCertify(selectedInvestor.id)}
                          data-testid="certify-btn"
                        >
                          <ShieldCheck className="h-4 w-4 mr-1" />
                          {t('social_investor_registry.cert_grant')}
                        </Button>
                      )}
                      
                      {(user?.role === 'administrator') && 
                       selectedInvestor.certification_level && 
                       selectedInvestor.certification_level !== 'none' && (
                        <Button 
                          size="sm"
                          variant="destructive"
                          onClick={() => handleRevokeCertification(selectedInvestor.id)}
                          data-testid="revoke-certification-btn"
                        >
                          <ShieldX className="h-4 w-4 mr-1" />
                          {t('social_investor_registry.cert_revoke')}
                        </Button>
                      )}
                      
                      {selectedInvestor.certification_level && 
                       selectedInvestor.certification_level !== 'none' && (
                        <Button 
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadCertificate(selectedInvestor.id)}
                          data-testid="download-certificate-btn"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          {t('social_investor_registry.cert_download_certificate')}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Assessment Scores */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" />
                      {t('social_investor_registry.scores_title')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {selectedInvestor.assessment_scores ? (
                      <div className="space-y-4">
                        {/* Overall Score */}
                        <div className="p-4 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg border">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium">{t('social_investor_registry.scores_overall')}</span>
                            <div className="flex items-center gap-3">
                              <span className="text-2xl font-bold text-emerald-700">
                                {selectedInvestor.assessment_scores.overall}%
                              </span>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 text-xs"
                                onClick={() => setShowBreakdown(true)}
                                data-testid="view-investor-score-breakdown-btn"
                              >
                                <Eye className="h-3 w-3 mr-1" /> View Breakdown
                                {(user?.role === 'administrator' || user?.role === 'admin') ? ' & Override' : ''}
                              </Button>
                            </div>
                          </div>
                          <Progress 
                            value={selectedInvestor.assessment_scores.overall} 
                            className="h-3"
                          />
                          <p className="text-xs text-gray-500 mt-2">
                            {selectedInvestor.assessment_scores.overall >= 80 
                              ? t('social_investor_registry.scores_eligible_a')
                              : selectedInvestor.assessment_scores.overall >= 60 
                              ? t('social_investor_registry.scores_eligible_b')
                              : selectedInvestor.assessment_scores.overall >= 40
                              ? t('social_investor_registry.scores_eligible_c')
                              : t('social_investor_registry.scores_below_threshold')}
                          </p>
                        </div>

                        {/* Individual Scores */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {Object.entries(scoreLabels).map(([key, label]) => {
                            const score = selectedInvestor.assessment_scores[key] || 0;
                            return (
                              <div key={key} className="p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm text-gray-600">{label}</span>
                                  <span className={`text-sm font-medium ${
                                    score >= 80 ? 'text-emerald-700' :
                                    score >= 60 ? 'text-blue-600' :
                                    score >= 40 ? 'text-amber-600' :
                                    'text-red-600'
                                  }`}>
                                    {score}%
                                  </span>
                                </div>
                                <Progress value={score} className="h-2" />
                              </div>
                            );
                          })}
                        </div>

                        {/* Last Assessment Date */}
                        {selectedInvestor.last_assessment_date && (
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {t('social_investor_registry.scores_last_assessed', { date: new Date(selectedInvestor.last_assessment_date).toLocaleString('en-GB') })}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 mb-4">{t('social_investor_registry.scores_no_assessments')}</p>
                        <Button 
                          size="sm" 
                          onClick={() => handleCalculateScore(selectedInvestor.id)}
                        >
                          <Calculator className="h-4 w-4 mr-1" />
                          {t('social_investor_registry.scores_calculate_now')}
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Certification Requirements Info */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">{t('social_investor_registry.cert_req_title')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Shield className="h-5 w-5 text-amber-600" />
                          <span className="font-medium text-amber-800">{t('social_investor_registry.cert_req_c_label')}</span>
                        </div>
                        <p className="text-amber-700">{t('social_investor_registry.cert_req_c_score')}</p>
                        <p className="text-xs text-amber-600 mt-1">{t('social_investor_registry.cert_req_c_desc')}</p>
                      </div>
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center gap-2 mb-2">
                          <ShieldCheck className="h-5 w-5 text-blue-600" />
                          <span className="font-medium text-blue-800">{t('social_investor_registry.cert_req_b_label')}</span>
                        </div>
                        <p className="text-blue-700">{t('social_investor_registry.cert_req_b_score')}</p>
                        <p className="text-xs text-blue-600 mt-1">{t('social_investor_registry.cert_req_b_desc')}</p>
                      </div>
                      <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                        <div className="flex items-center gap-2 mb-2">
                          <ShieldCheck className="h-5 w-5 text-emerald-700" />
                          <span className="font-medium text-emerald-800">{t('social_investor_registry.cert_req_a_label')}</span>
                        </div>
                        <p className="text-emerald-700">{t('social_investor_registry.cert_req_a_score')}</p>
                        <p className="text-xs text-emerald-700 mt-1">{t('social_investor_registry.cert_req_a_desc')}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="matches" className="mt-4">
                <InvestorMatches investorId={selectedInvestor.id} />
              </TabsContent>

              <TabsContent value="activity" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">{t('social_investor_registry.activity_log_title')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {selectedInvestor.activity_log && selectedInvestor.activity_log.length > 0 ? (
                      <div className="space-y-3">
                        {selectedInvestor.activity_log.slice().reverse().map((log, idx) => (
                          <div key={idx} className="flex items-start gap-3 text-sm border-b pb-2">
                            <div className="bg-gray-100 p-1 rounded">
                              <BarChart3 className="h-3 w-3" />
                            </div>
                            <div>
                              <p className="font-medium capitalize">{log.action?.replace('_', ' ')}</p>
                              <p className="text-gray-500 text-xs">{log.details}</p>
                              <p className="text-gray-500 text-xs">{new Date(log.timestamp).toLocaleString('en-GB')}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">{t('social_investor_registry.activity_no_recorded')}</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Score Breakdown & Override Modal */}
      {selectedInvestor && (
        <ScoreBreakdownModal
          open={showBreakdown}
          onClose={() => setShowBreakdown(false)}
          entityType="investor"
          entityId={selectedInvestor.id}
          entityName={selectedInvestor.organization_name}
          isAdmin={user?.role === 'administrator' || user?.role === 'admin'}
          onChanged={() => {
            if (selectedInvestor?.id) {
              axios.get(`${API}/api/social-investors/${selectedInvestor.id}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
              }).then(res => setSelectedInvestor(res.data)).catch(() => {});
            }
          }}
        />
      )}
    </div>
  );
};

export default SocialInvestorRegistry;
